import type * as React from 'react';
import { useState } from 'react';
import { X } from 'lucide-react';
import { ChatCollapseIcon, ChatExpandIcon } from './chat-drawer-icons.tsx';
import { WundoChatIcon } from './wundo-chat-icon.tsx';
import {
  CHAT_DRAWER_MAX_WIDTH,
  CHAT_DRAWER_MIN_WIDTH,
  CHAT_STRIP_WIDTH,
  chatDrawerDefaultWidth,
} from './chatDrawerWidth.ts';
import { containTab } from './containTab.ts';
import { followInApp } from './followInApp.ts';
import { cn } from '../../lib/cn.ts';
import { CHAT_COLUMN_CLASS } from './chatColumn.ts';
import { useBelowMobile } from '../../lib/useBelowMobile.ts';
import { useVisibleArea } from '../../lib/useVisibleArea.ts';
import { IconButton } from '../icon-button/icon-button.tsx';
import { ResizeHandle } from '../resize-handle/resize-handle.tsx';

/** How far one arrow key moves the drag handle, and how far with shift held. */
const RESIZE_STEP = 16;
const RESIZE_SHIFT_STEP = 48;

export interface ChatDrawerProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  open: boolean;
  /** Shown in the header. Also names the region for screen readers. */
  title: string;
  /** The page's invitation to ask something. Left out on pages that have none. */
  subtitle?: string;
  /** The header control on a phone, where dismissing the drawer closes it. */
  closeLabel: string;
  /** The header control on a desktop, where dismissing it leaves the strip. */
  collapseLabel: string;
  /** Names the collapsed strip — the whole strip is the way back in. */
  stripLabel: string;
  /** Names the drag handle. Required alongside `onWidthChange`. */
  resizeLabel?: string;
  /** Collapse on a desktop, close on a phone. Escape asks for the same thing. */
  onDismiss: () => void;
  /** Open it again from the strip. */
  onExpand: () => void;
  /**
   * How wide it is drawn, in pixels. Ignored where the drawer covers the page.
   * Left out to open at the width the design picks for this window.
   */
  width?: number;
  /** Left out to make the drawer a fixed width. */
  onWidthChange?: (width: number) => void;
  /** Home or a double-click on the handle: back to the width nobody chose. */
  onWidthReset?: () => void;
  /** The strip is where the caret goes when the drawer collapses under it. */
  stripRef?: React.Ref<HTMLButtonElement>;
  /** Something arrived while the drawer was away. Marked on the strip. */
  unread?: boolean;
  /** Says the same thing out loud. Required alongside `unread`. */
  unreadLabel?: string;
  /** The earlier conversations. Only drawn in full screen, where there is room. */
  sidebar?: React.ReactNode;
  /** Extra header controls, left of the dismiss button — history, new, full screen. */
  action?: React.ReactNode;
  /** Covers the whole window, for a conversation worth giving the screen to. */
  expanded?: boolean;
}

/**
 * The panel Wundo is talked to in: fixed to the right edge, full height.
 *
 * It has three presentations rather than two. Open, it is a working column: as
 * wide as it has been dragged, with `AppShell` giving it a track of its own
 * wherever the page still fits beside it. Collapsed, it slides off until only a
 * 44px strip is left — the strip is the way back in, and it means there is a
 * Wundo on every page instead of a bubble floating over one. On a phone there
 * is no room for a strip, so collapsing is closing and the panel leaves
 * entirely; the way back in lives in the topbar.
 *
 * Expanded, it takes everything but the rail. Losing the navigation in order to
 * read is a bad trade, so the rail stays and the drawer starts at its edge. The
 * panel grows but the conversation does not: it keeps a column of readable
 * width, because a line of text spanning a wide screen is harder to read, not
 * easier — and the room that buys goes to the earlier conversations, standing
 * open beside it rather than behind a button.
 *
 * Collapsed it is parked off-screen rather than removed, because the
 * conversation inside it has to survive being put away. What that costs is a
 * panel still in the DOM, still focusable and still read aloud — so its
 * contents are made inert and hidden from screen readers, leaving the strip as
 * the only thing anyone can reach.
 *
 * Escape dismisses it wherever it stands. On a phone it also behaves as a
 * modal — announced as one, and holding the tab order — because there is
 * nothing else left on screen to reach. Anywhere else it does neither: there is
 * always something beside it being read or navigated with, and calling that a
 * modal would be a lie.
 */
export function ChatDrawer({
  open,
  title,
  subtitle,
  closeLabel,
  collapseLabel,
  stripLabel,
  resizeLabel,
  onDismiss,
  onExpand,
  width,
  onWidthChange,
  onWidthReset,
  stripRef,
  unread = false,
  unreadLabel,
  sidebar,
  action,
  expanded = false,
  className,
  children,
  onKeyDown,
  ...props
}: ChatDrawerProps) {
  const belowMobile = useBelowMobile();
  /*
   * The width for a drawer nobody has sized, worked out once when it first
   * appears. Read at mount rather than on every render: it is the width the
   * drawer opens at, and a window being dragged wider mid-conversation must not
   * make the panel creep along with it.
   */
  const [openingWidth] = useState(chatDrawerDefaultWidth);
  const drawnWidth = width ?? openingWidth;
  /* Expanded, or on a phone: either way it is no longer a column of its own width. */
  const filling = expanded || belowMobile;
  /*
   * A phone leaves nothing else on screen at all, so there the drawer really is
   * a modal — announced as one, and holding the tab order, because the page
   * behind it cannot be seen or dismissed. Full screen is not: it keeps the
   * rail, and the whole reason it keeps the rail is that navigation stays
   * usable. Holding the tab order away from it would take back with one hand
   * what the layout gives with the other.
   */
  const modal = belowMobile;
  /*
   * Covering a phone, it is sized to what is visible rather than to the window:
   * the iOS keyboard pans a full-height fixed panel up, header and all, and the
   * composer jumps with every line typed.
   */
  const area = useVisibleArea(belowMobile && open);
  /* A phone has no room for a strip, so there it goes all the way out. */
  const parked = belowMobile
    ? 'translate-x-full'
    : 'translate-x-[calc(100%-var(--chat-strip))]';

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);

    if (event.key === 'Escape' && open) {
      onDismiss();
      return;
    }

    if (event.key === 'Tab' && modal && open) {
      containTab(event);
    }
  }

  return (
    /*
     * A div rather than an <aside>: the drawer is a complementary region most
     * of the time and a dialog on a phone, and an <aside> may not be given the
     * dialog role. Saying the region role out loud costs nothing and lets the
     * one element carry both.
     */
    <div
      data-slot='chat-drawer'
      data-open={open}
      data-expanded={expanded}
      data-keyboard={area?.keyboard ?? false}
      aria-label={title}
      role={modal && open ? 'dialog' : 'complementary'}
      aria-modal={modal && open ? true : undefined}
      onKeyDown={handleKeyDown}
      style={
        {
          /*
           * Full screen stops at the rail rather than at the window — the same
           * `--rail-w` token the shell lays the rail out with. Said as a width
           * from the right edge rather than a `left` inset: both states must
           * size the panel the same way, in lengths, or the width transition
           * below has nothing to interpolate and leaving full screen snaps.
           * (`100%` of a fixed element is the viewport without its scrollbar,
           * which `100vw` is not.)
           *
           * The 92vw cap — a dragged-wide drawer never quite reaches the far
           * edge of a narrow window — lives in the same expression for the same
           * reason: as a `max-width` it would snap on rather than interpolate.
           */
          width: expanded
            ? belowMobile
              ? '100%'
              : 'calc(100% - var(--rail-w))'
            : belowMobile
              ? undefined
              : `min(${drawnWidth}px, 92vw)`,
          '--chat-strip': `${CHAT_STRIP_WIDTH}px`,
          ...(area ? { top: area.top, height: area.height, bottom: 'auto' } : {}),
        } as React.CSSProperties
      }
      className={cn(
        /*
         * Below the impersonation banner, like the rail: over it, the drawer
         * would cover the banner's stop button. `--app-banner-h` is 0 without one.
         */
        'fixed top-[var(--app-banner-h,0px)] bottom-0 right-0 z-30 flex flex-col overscroll-contain touch-manipulation',
        /*
         * Width animates too: leaving full screen is a size change, not a
         * slide. `translate` must be named — the slide classes set the CSS
         * `translate` property, which a bare `transform` does not cover.
         */
        'transition-[translate,transform,width] duration-200 ease-out',
        /*
         * Sized to the visible area, the panel stops where the keyboard begins.
         * iOS draws its autofill bar there see-through, so the panel's own
         * surface runs on under it instead of the page behind.
         */
        'max-mobile:after:absolute max-mobile:after:inset-x-0 max-mobile:after:top-full max-mobile:after:h-screen max-mobile:after:bg-inherit',
        open ? 'translate-x-0' : parked,
        expanded
          ? 'bg-canvas'
          : 'bg-surface border-l border-line shadow-drawer max-mobile:w-screen',
        className,
      )}
      {...props}
    >
      {open && !filling && onWidthChange && resizeLabel ? (
        <ResizeHandle
          label={resizeLabel}
          value={drawnWidth}
          min={CHAT_DRAWER_MIN_WIDTH}
          max={CHAT_DRAWER_MAX_WIDTH}
          defaultValue={openingWidth}
          step={RESIZE_STEP}
          shiftStep={RESIZE_SHIFT_STEP}
          onChange={onWidthChange}
          onReset={onWidthReset}
        />
      ) : null}

      {/*
        The mark says which panel is parked there, the chevron says it opens —
        both always drawn, because an affordance that only appears on hover does
        not exist for anyone who cannot hover. The whole strip is the hit area.
      */}
      {belowMobile ? null : (
        <>
          <button
            ref={stripRef}
            data-slot='chat-strip'
            type='button'
            aria-label={stripLabel}
            aria-expanded={open}
            onClick={onExpand}
            className={cn(
              open ? 'hidden' : 'flex',
              'absolute inset-y-0 left-0 z-10 w-11 flex-col items-center gap-2.5 pt-9',
              'cursor-pointer text-ink transition-colors hover:bg-fill-faint',
              'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
            )}
          >
            <WundoChatIcon size={20} />
            <ChatExpandIcon size={14} className='text-ink-faint' />
            {/*
            Something came in while the drawer was away. A dot rather than a
            count: what is waiting is a reply, and how many is not the question.
          */}
            {unread ? (
              <span
                data-slot='chat-strip-dot'
                aria-hidden='true'
                className='absolute top-8.25 right-1.75 size-1.75 rounded-full bg-accent'
              />
            ) : null}
          </button>
          {/*
          And said out loud, because a green dot is not an announcement. Beside
          the strip rather than inside it: the strip carries a label, and a
          label is what a screen reader reads instead of the contents.
        */}
          {unread && !open && unreadLabel ? (
            <span role='status' className='sr-only'>
              {unreadLabel}
            </span>
          ) : null}
        </>
      )}

      <div
        data-slot='chat-drawer-content'
        /* Parked, nothing in here may be tabbed to, read out, or clicked through the strip. */
        aria-hidden={!open}
        inert={!open}
        className={cn(
          'flex min-h-0 flex-1 transition-opacity duration-150',
          expanded ? 'flex-row' : 'flex-col',
          open ? 'opacity-100' : 'opacity-0',
        )}
      >
        {expanded ? sidebar : null}
        {/*
          Docked there is only one column, so this wrapper must not exist —
          `contents` takes it out of the layout and leaves the header, the body
          and the composer as the drawer's own children, exactly as before.
        */}
        <div className={expanded ? 'flex min-w-0 flex-1 flex-col' : 'contents'}>
          <div
            data-slot='chat-drawer-header'
            /* Clear of the status bar when the app runs from the home screen. */
            className='relative shrink-0 border-b border-line px-5.5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-4'
          >
            {/* Expanded, the header lines up with the conversation below it. */}
            <div className={cn('relative mx-auto w-full', expanded && 'max-w-190')}>
              <h2 className='pr-18 text-base font-semibold text-ink'>{title}</h2>
              {subtitle ? (
                <p className='mt-0.75 pr-18 text-sm text-ink-muted'>{subtitle}</p>
              ) : null}
            </div>
            <div className='absolute top-[max(1.125rem,calc(env(safe-area-inset-top)-0.375rem))] right-4 flex items-center gap-0.5'>
              {action}
              <IconButton
                aria-label={belowMobile ? closeLabel : collapseLabel}
                onClick={onDismiss}
              >
                {belowMobile ? (
                  <X size={16} aria-hidden='true' />
                ) : (
                  <ChatCollapseIcon size={16} />
                )}
              </IconButton>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export interface ChatDrawerBodyProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /** Must match the drawer's own `expanded`. */
  expanded?: boolean;
  /**
   * Follows a link in the conversation that points at a page of this same app,
   * given the path it points at. Left out — in a story, or anywhere without a
   * router — such a link loads its page the ordinary way instead, which works
   * but costs the conversation.
   */
  onNavigate?: (path: string) => void;
}

/**
 * The scrolling middle of the drawer.
 *
 * Empty, it is empty: no greeting, no card, nothing standing in for a
 * conversation that has not started. What there is to ask sits in the pills
 * above the composer, where the hand already is.
 *
 * Expanded, the scroll still belongs to the full width — a scrollbar hugging a
 * centred column looks like a mistake — so the column is inside it.
 */
export function ChatDrawerBody({
  expanded = false,
  onNavigate,
  className,
  children,
  onClick,
  ...props
}: ChatDrawerBodyProps) {
  function handleClick(event: React.MouseEvent<HTMLDivElement>) {
    onClick?.(event);

    if (onNavigate) {
      followInApp(event, onNavigate);
    }
  }

  return (
    <div
      data-slot='chat-drawer-body'
      onClick={handleClick}
      /*
       * `min-h-0` so it actually scrolls: a flex child refuses to shrink below
       * its content by default, and the conversation would then push the
       * composer down the page instead of scrolling under it.
       */
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5.5 py-5 [overflow-anchor:none]',
        className,
      )}
      {...props}
    >
      <div className={cn(expanded && CHAT_COLUMN_CLASS)}>{children}</div>
    </div>
  );
}

export interface ChatDrawerFooterProps extends Omit<
  React.ComponentProps<'div'>,
  'style'
> {
  /** Must match the drawer's own `expanded`. */
  expanded?: boolean;
}

/** Holds the composer, and stays put while the body scrolls. */
export function ChatDrawerFooter({
  expanded = false,
  className,
  children,
  ...props
}: ChatDrawerFooterProps) {
  return (
    <div
      data-slot='chat-drawer-footer'
      /*
       * Above the home indicator — except with the keyboard up, which covers it
       * and would leave the inset as a gap under the composer.
       */
      className={cn(
        'shrink-0 border-t border-line px-4 pt-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))]',
        'in-data-[keyboard=true]:pb-3.5',
        className,
      )}
      {...props}
    >
      <div className={cn(expanded && CHAT_COLUMN_CLASS)}>{children}</div>
    </div>
  );
}

export type ChatDisclaimerProps = Omit<React.ComponentProps<'p'>, 'style'>;

export function ChatDisclaimer({ className, ...props }: ChatDisclaimerProps) {
  return (
    <p
      data-slot='chat-disclaimer'
      className={cn('mt-2 text-center text-xs text-ink-muted', className)}
      {...props}
    />
  );
}
